import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useCallback, useState} from 'react'

import {LABELS} from './constants/labels'
import {DialogForm as CreateDialog} from './DialogForm'
import type {RepositoryNode} from './repository-label-index-page-query-types'
import {useCreateLabel} from './use-create-label'

export type LabelCreateFragment = Pick<RepositoryNode, 'id' | 'nameWithOwner' | 'viewerCanCreateLabels'>

type LabelCreateProps = {
  repository: LabelCreateFragment
  isOpen: boolean
  setDialogOpen: (open: boolean) => void
  queryKey?: RouteQueryKey
}

export function LabelCreate({repository, isOpen, setDialogOpen, queryKey}: LabelCreateProps) {
  const [submissionErrors, setSubmissionErrors] = useState('')
  const [lastInput, setLastInput] = useState<{name: string; description: string; color: string} | undefined>()

  const createLabel = useCreateLabel({
    repositoryId: repository.id,
    repositoryNameWithOwner: repository.nameWithOwner,
    queryKey,
    setSubmissionErrors,
    setDialogOpen,
    onSuccess: () => setLastInput(undefined),
  })

  const handleSubmit = useCallback(
    async (
      input: {name: string; description: string; color: string},
      setIsSubmitting: (isSubmitting: boolean) => void,
    ) => {
      setSubmissionErrors('')
      setLastInput(input)

      if (!repository.viewerCanCreateLabels) {
        setSubmissionErrors(LABELS.labelCreatePermissionError)
        setIsSubmitting(false)
        return
      }

      await createLabel(input, setIsSubmitting)
    },
    [repository, createLabel],
  )

  const handleClose = useCallback(() => {
    setSubmissionErrors('')
    setLastInput(undefined)
    setDialogOpen(false)
  }, [setDialogOpen])

  if (!isOpen) {
    return null
  }

  return (
    <CreateDialog
      onDialogClose={handleClose}
      formTitle={LABELS.newLabel}
      namePlaceholder={LABELS.labelNamePlaceholder}
      descriptionPlaceholder={LABELS.labelDescriptionPlaceholder}
      submissionErrors={submissionErrors}
      submitButtonText={LABELS.createButtonText}
      onDialogSubmit={handleSubmit}
      initialValues={lastInput}
    />
  )
}
